import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { AdviseSupplier } from '../types/advise';
import {
  fetchAdviseSuppliers as apiFetchAdviseSuppliers,
  createAdviseSupplier as apiCreateAdviseSupplier,
  updateAdviseSupplier as apiUpdateAdviseSupplier,
  deleteAdviseSupplier as apiDeleteAdviseSupplier
} from '../Api/Advise/Suppliers';
import { CallResult } from '../Api/callExternalApi';
import { addError } from './Error';

export interface UseAdviseSuppliersReturn {
  suppliers: AdviseSupplier[];
  fetchSuppliers: (forceRefresh?: boolean) => Promise<void>;
  createSupplier: (supplier: Partial<AdviseSupplier>) => Promise<CallResult<AdviseSupplier>>;
  updateSupplier: (id: string, supplier: Partial<AdviseSupplier>) => Promise<CallResult<AdviseSupplier>>;
  deleteSupplier: (id: string) => Promise<boolean>;
  clear: () => void;
}

const defaultData: AdviseSupplier[] = [];
const subject = new BehaviorSubject<AdviseSupplier[]>(defaultData);
const loadingSubject = new BehaviorSubject<boolean>(false);
const emitErrorState = (message?: string): void => addError('advise-supplier', message);

export const getAdviseSupplierSubject = (): BehaviorSubject<AdviseSupplier[]> => subject;
export const getAdviseSuppliers = (): AdviseSupplier[] => subject.value;
export const getAdviseSupplierLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;

// The list a load returns predates these, so they are applied on top of it.
let savedWhileLoading: AdviseSupplier[] = [];
const rememberWhileLoading = (rows: AdviseSupplier[]): void => {
  if (loadingSubject.getValue()) savedWhileLoading = [...savedWhileLoading, ...rows];
};

const withUpserts = (current: AdviseSupplier[], rows: AdviseSupplier[]): AdviseSupplier[] => {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const listed = new Set(current.map((supplier) => supplier.id));
  return [
    ...current.map((supplier) => byId.get(supplier.id) ?? supplier),
    ...rows.filter((row) => !listed.has(row.id))
  ];
};

/** Replaces the suppliers already listed and adds the rest, as a save that made or changed them returns them. */
export const upsertAdviseSuppliers = (rows: AdviseSupplier[]): void => {
  rememberWhileLoading(rows);
  subject.next(withUpserts(subject.getValue(), rows));
};

export const useAdviseSuppliers = (): UseAdviseSuppliersReturn => {
  const [suppliers, setSuppliers] = useState(subject.getValue());

  useEffect(() => {
    const sub = subject.subscribe(setSuppliers);
    return () => sub.unsubscribe();
  }, []);

  const fetchSuppliers = async (forceRefresh = false): Promise<void> => {
    if (loadingSubject.getValue()) return;
    if (subject.getValue().length > 0 && !forceRefresh) return;
    loadingSubject.next(true);
    savedWhileLoading = [];

    const { data, error } = await apiFetchAdviseSuppliers();
    if (data) {
      emitErrorState();
      const loaded = withUpserts(data, savedWhileLoading);
      subject.next(loaded);
      setSuppliers(loaded);
    }
    if (error) {
      subject.next(defaultData);
      emitErrorState(error.message);
    }
    loadingSubject.next(false);
  };

  const createSupplier = async (supplier: Partial<AdviseSupplier>): Promise<CallResult<AdviseSupplier>> => {
    const result = await apiCreateAdviseSupplier(supplier);
    const { data, error } = result;
    if (data) {
      emitErrorState();
      rememberWhileLoading([data]);
      const updated = [...subject.getValue(), data];
      subject.next(updated);
      setSuppliers(updated);
    }
    if (error) emitErrorState(error.message);
    return result;
  };

  const updateSupplier = async (id: string, supplier: Partial<AdviseSupplier>): Promise<CallResult<AdviseSupplier>> => {
    const result = await apiUpdateAdviseSupplier(id, supplier);
    const { data, error } = result;
    if (data) {
      emitErrorState();
      rememberWhileLoading([data]);
      const updated = subject.getValue().map((v) => (v.id === id ? data : v));
      subject.next(updated);
      setSuppliers(updated);
    }
    if (error) emitErrorState(error.message);
    return result;
  };

  const deleteSupplier = async (id: string): Promise<boolean> => {
    const { error } = await apiDeleteAdviseSupplier(id);
    if (!error) {
      emitErrorState();
      const updated = subject.getValue().filter((v) => v.id !== id);
      subject.next(updated);
      setSuppliers(updated);
      return true;
    }
    emitErrorState(error.message);
    return false;
  };

  const clear = () => {
    subject.next(defaultData);
    setSuppliers(defaultData);
  };

  return { suppliers, fetchSuppliers, createSupplier, updateSupplier, deleteSupplier, clear };
};
